"use client"

import type { ReactNode } from "react"
import Link from "next/link"
import { ArrowLeft, Package } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { APP_NAME } from "@/lib/app-config"

interface AuthCardShellProps {
    title: ReactNode
    description?: ReactNode
    backHref?: string
    backLabel?: string
    children: ReactNode
}

/**
 * Shared card shell for all auth pages.
 * Renders: optional back link → platform logo → title → description → children.
 */
export function AuthCardShell({ title, description, backHref, backLabel, children }: AuthCardShellProps) {
    return (
        <Card className="w-full max-w-md shadow-lg">
            <CardHeader className="space-y-1">
                {backHref && backLabel && (
                    <div className="flex items-center gap-2 mb-2">
                        <Link href={backHref}>
                            <Button
                                variant="ghost"
                                size="sm"
                                className="gap-2 pl-0 hover:bg-transparent text-muted-foreground hover:text-primary"
                            >
                                <ArrowLeft className="h-4 w-4" />
                                {backLabel}
                            </Button>
                        </Link>
                    </div>
                )}

                <div className="flex items-center gap-3 mb-4">
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary text-primary-foreground">
                        <Package className="h-6 w-6" />
                    </div>
                    <span className="typo-brand-title">{APP_NAME}</span>
                </div>

                <CardTitle className="typo-section-title">{title}</CardTitle>
                {description && <CardDescription>{description}</CardDescription>}
            </CardHeader>

            <CardContent>{children}</CardContent>
        </Card>
    )
}
